import React, { ReactNode, useEffect, useRef } from 'react'
import styles from './panels/socialMatchesPanels.module.scss'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'
import { usePaginatedFetch } from 'hooks/use-fetch'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { MatchUpSubmissionStateEnum } from 'types/api/enums'
import { Paging } from 'types/global'
import SocialMatchCard from './SocialMatchCard'
import LoadMore from 'components/generic/LoadMore'
import useWindowSize from 'hooks/use-windowSize'
import { MOBILE_BREAKPOINT } from 'constants/global'
import { SOCIAL_MATCHES_FETCH_LIMIT } from 'constants/profile'

interface Props {
  type: MatchUpSubmissionStateEnum
  fetch: ({ skip, limit }: Paging) => void
  stateMappingKey: string
  renderIfNoMatches?: ReactNode
}

const SocialMatchesContainer = ({ type, fetch, stateMappingKey, renderIfNoMatches }: Props) => {
  const hasFetchedOnMount = useRef(false)
  const profileViewedId = useSelector((state: AppState) => state.profile.loadedProfile?.id)
  const allLoadedMatchesForThisType = useSelector(
    (state: AppState) => state.socialMatches.matchesLoaded?.[stateMappingKey]?.[type] || []
  )
  const matchesUpdating = useSelector((state: AppState) => state.socialMatches.matchesUpdating)
  const isLoadingMatches = useSelector(
    (state: AppState) => state.socialMatches.isLoadingMatches[type]
  )
  const totalMatchesForThisType = useSelector(
    (state: AppState) => state.socialMatches.totals[stateMappingKey]?.[type]
  )
  const windowWidth = useWindowSize().width

  const [loadMatches, , areAllLoaded] = usePaginatedFetch(
    {
      fetch,
      totalFetched:
        allLoadedMatchesForThisType.length >= totalMatchesForThisType
          ? allLoadedMatchesForThisType.length
          : Math.max(allLoadedMatchesForThisType.length - 6, 0),
      pageSize: Math.min(allLoadedMatchesForThisType.length, 6) + SOCIAL_MATCHES_FETCH_LIMIT,
      total: totalMatchesForThisType || 0,
      invalidateAutoFetch: true,
    },
    []
  )

  useEffect(() => {
    if (!hasFetchedOnMount.current) {
      fetch({ skip: 0, limit: SOCIAL_MATCHES_FETCH_LIMIT })
      hasFetchedOnMount.current = true
    } else if (
      typeof totalMatchesForThisType === 'number' &&
      totalMatchesForThisType > 0 &&
      !allLoadedMatchesForThisType.length &&
      !isLoadingMatches
    ) {
      fetch({ skip: 0, limit: SOCIAL_MATCHES_FETCH_LIMIT })
    }
  }, [totalMatchesForThisType, allLoadedMatchesForThisType, fetch, isLoadingMatches])

  if (isLoadingMatches && !allLoadedMatchesForThisType.length) {
    return <LoadingSpinnerWithContainer containerHeight={300} />
  }

  if (!allLoadedMatchesForThisType.length && renderIfNoMatches) {
    return <>{renderIfNoMatches}</>
  }

  return (
    <>
      <ul className={styles.cardContainer}>
        {allLoadedMatchesForThisType.map(match => (
          <li key={match.id} className={styles.cardItem}>
            <SocialMatchCard
              profileViewedId={profileViewedId || ''}
              match={match}
              isUpdating={matchesUpdating[match.id]}
              truncateName={windowWidth < MOBILE_BREAKPOINT}
            />
          </li>
        ))}
      </ul>
      {!areAllLoaded && (
        <div className={styles.loadMoreBtnWrapper}>
          <LoadMore loading={isLoadingMatches} loadMore={loadMatches} />
        </div>
      )}
    </>
  )
}

export default SocialMatchesContainer
